/*
 * Scroll infinito para as grelhas de produtos (coleções, páginas e pesquisa).
 *
 * Usa a Section Rendering API: pede o URL da página seguinte (`paginate.next.url`,
 * que preserva filtros, ordenação e parâmetros como `?parent=`) com `section_id`,
 * extrai os itens da grelha devolvida e anexa-os à grelha atual.
 *
 * O elemento vive dentro de #ProductGridContainer, por isso o facets.js pode
 * substituir o container ao filtrar que o componente volta a inicializar sozinho.
 */
class ProductGridInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    this.grid = this.closest('.collection')?.querySelector('ul.product-grid');
    this.button = this.querySelector('[data-infinite-scroll-button]');
    this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
    this.status = this.querySelector('[data-infinite-scroll-status]');

    if (!this.grid || !this.nextUrl) return;

    // Com JS ativo o carregamento é automático; o link fica só como fallback
    // (sem JS e para os crawlers) e reaparece se um pedido falhar.
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
      this.button.classList.add('hidden');
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.button) this.button.removeEventListener('click', this.onButtonClick);
  }

  get nextUrl() {
    return this.dataset.nextUrl;
  }

  set nextUrl(value) {
    if (value) {
      this.dataset.nextUrl = value;
      if (this.button) this.button.href = value;
    } else {
      delete this.dataset.nextUrl;
      this.finish();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    this.load();
  }

  async load() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.setBusy(true);

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Pedido falhou com o estado ${response.status}`);

      const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
      const appended = this.append(document_);

      // Sem itens novos não há nada a paginar, mesmo que exista URL seguinte.
      this.nextUrl = appended > 0 ? this.getNextUrl(document_) : null;
    } catch (error) {
      console.error('Erro ao carregar mais produtos:', error);
      this.showRetry();
      return;
    } finally {
      this.loading = false;
      this.setBusy(false);
    }

    // O IntersectionObserver só dispara em mudanças de estado: se o elemento
    // continuar visível depois de anexar (ecrãs altos, poucas colunas), continua.
    if (this.nextUrl && this.isNearViewport()) {
      requestAnimationFrame(() => this.load());
    }
  }

  append(document_) {
    const newGrid =
      document_.querySelector('#ProductGridContainer ul.product-grid') || document_.querySelector('ul.product-grid');
    if (!newGrid) return 0;

    const items = Array.from(newGrid.children);
    if (items.length === 0) return 0;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => {
      // Cada página reinicia o `skip_styles` do card-product e reinjeta os
      // stylesheets do card; já estão carregados pela secção.
      item.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
      // As animações de entrada são canceladas: os itens são anexados já visíveis.
      item.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.remove('scroll-trigger'));
      item.classList.remove('scroll-trigger');
      // Cada página marca as 2 primeiras imagens como eager; fora da 1.ª página
      // não faz sentido dar-lhes prioridade de carregamento.
      item.querySelectorAll('img[loading="eager"]').forEach((image) => image.setAttribute('loading', 'lazy'));
      fragment.appendChild(item);
    });

    this.grid.appendChild(fragment);

    if (this.status) {
      this.status.textContent = `${this.grid.children.length} produtos carregados`;
    }

    return items.length;
  }

  getNextUrl(document_) {
    const next = document_.querySelector('product-grid-infinite-scroll');
    return next ? next.dataset.nextUrl : null;
  }

  isNearViewport() {
    const { top } = this.getBoundingClientRect();
    return top <= window.innerHeight + 600;
  }

  setBusy(isLoading) {
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
    this.setAttribute('aria-busy', String(isLoading));
  }

  showRetry() {
    if (this.button) {
      this.button.classList.remove('hidden');
      this.button.textContent = 'Tentar novamente';
    }
    if (this.observer) this.observer.disconnect();
  }

  finish() {
    if (this.observer) this.observer.disconnect();
    if (this.button) this.button.classList.add('hidden');
    this.classList.add('infinite-scroll--done');
  }
}

customElements.define('product-grid-infinite-scroll', ProductGridInfiniteScroll);
